import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEffectOnce } from 'usehooks-ts';
import { isEmpty, filter, isUndefined } from 'lodash';
import { Row, Col } from 'react-bootstrap';
import { Formik, Form, FormikHelpers } from 'formik';
import DataTable from 'react-data-table-component';
import { FormGroupControl, FormGroupTypeahead } from '@components/Base';
import { CorporateHealthOrder } from '@models/CorporateHealthOrder';
import { sleep } from '@utils/promise';
import { formValuesSchema, FormValues } from './schema';
import { selectSearchCorporateMember } from '../selector';
import { corporateHealthOrdersFetch, corporateHealthOrderUsersFetch } from '../action';
import { useColumns } from '../view';

const GROUP_PROPS = { className: 'mb-3' };
const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

const SearchFormFrontlineUser = () => {
  const dispatch = useDispatch();
  const columns = useColumns();
  const { corporateHealthOrders, corporateHealthOrderUsers, pagination } = useSelector(selectSearchCorporateMember);
  const [isLoading, setIsLoading] = useState(false);
  const initialValues: FormValues = formValuesSchema.getDefault() as unknown as FormValues;

  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    setIsLoading(true);
    await sleep(1000);
    dispatch(corporateHealthOrderUsersFetch(values));
    setSubmitting(false);
  };

  const handlePageChange = (page: number, values: FormValues) => {
    setIsLoading(true);
    dispatch(corporateHealthOrderUsersFetch({ ...values, page }));
  };

  const handlePerRowsChange = (newPerPage: number, page: number, values: FormValues) => {
    setIsLoading(true);
    dispatch(corporateHealthOrderUsersFetch({ ...values, perPage: newPerPage, page }));
  };

  useEffectOnce(() => {
    dispatch(corporateHealthOrdersFetch());
  });

  useEffect(() => {
    if (isUndefined(corporateHealthOrderUsers)) return;
    setIsLoading(false);
  }, [corporateHealthOrderUsers]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={formValuesSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, handleChange, submitForm }) => (
        <Form>
          <Row>
            <Col>
              <FormGroupControl
                name="search"
                label={`Member search`}
                groupProps={GROUP_PROPS}
                placeholder="Search by name, username"
                onChange={(e) => {
                  handleChange(e);
                  (async () => {
                    await submitForm();
                  })();
                }}
              />
            </Col>
            <Col>
              <FormGroupTypeahead
                name="policyNumber"
                label={`Policy`}
                options={filter(corporateHealthOrders, (policy) => !isEmpty(policy.policyNumber)) ?? []}
                groupProps={GROUP_PROPS}
                labelKey={(option) =>
                  `${(option as CorporateHealthOrder).policyNumber as string} - ${
                    (option as CorporateHealthOrder).company?.name as string
                  }`
                }
                placeholder="Type or select"
                onChange={(selected) => {
                  (async () => {
                    await setFieldValue(
                      'policyNumber',
                      (selected[0] as CorporateHealthOrder | undefined)?.policyNumber ?? '',
                    );
                    await submitForm();
                  })();
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <DataTable
                columns={columns}
                data={corporateHealthOrderUsers ?? []}
                customStyles={customStyles}
                fixedHeader
                pagination
                paginationServer
                progressPending={isLoading}
                paginationPerPage={pagination?.perPage ?? 20}
                paginationTotalRows={pagination?.total ?? 0}
                onChangeRowsPerPage={(newPerPage, page) => handlePerRowsChange(newPerPage, page, values)}
                onChangePage={(page) => handlePageChange(page, values)}
              />
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};

export { SearchFormFrontlineUser };
